import React from 'react';
import {getStudentMetrics, getUserData} from '../../services/API';
import story_badge from "../../images/EndofAssessmentComp-img-02.svg";
import Modal from '../Modal';
import {COPY_STRINGS} from '../../constants/translatedCopy';
import {locales} from '../../constants/constants';
import BadgingProgress from '../Badging/BadgingProgress';
import {getRandomArrayElement, getRandomInt} from '../../library';
import { REWARD_LEVEL_TO_NUM_SESSIONS } from '../../services/badging/constants';
import { GetBrandedAppName } from '../../services/BrandingService';
import { getCopyByGradeband } from '../../services/ThemingService';

const minNumberForStoryCountEstimate = 5;

export default class StudentHistory extends React.Component {
  constructor (props){
    super(props);
    this.state = {
      displayText: [],
      showStats: false,
      wcpm: "",
      accuracy: "",
      numberOfStories: null,
      showModal: false,
      praise: `${COPY_STRINGS.getting_history}...`,
      badgeSectionHighlighted: false,
      nextBadgeHighlighted: false,
      badgeLevelHighlighted: false,
    };

    this.getProgressTextToSpeak = this.getProgressTextToSpeak.bind(this);
    this.getNumberStoriesFromBadges = this.getNumberStoriesFromBadges.bind(this);

    // This page renders in the session's locale, so it must only count that locale's activities:
    // otherwise a bilingual student's Progreso page reports their English reading stats. A session
    // with no locale is English.
    getStudentMetrics(null, false, getUserData().locale || locales.EN_US).then(async resp =>{
      let numberOfTests = resp.testsTaken;
      let numberOfStories = resp.storiesRead;
      let displayText = this.getDisplayText();
      this.setState({
        displayText: this.processDisplayText(displayText),
        wcpm: resp.avgWPM,
        accuracy: resp.avgAccuracy,
        showStats: (numberOfStories || numberOfTests),
        showModal: true,
        numberOfStories: numberOfStories + numberOfTests,
        praise: (numberOfStories || numberOfTests) ? COPY_STRINGS.great_work : COPY_STRINGS.nice_to_meet_you,
      });
      if(!this.props.session.badgingEnabled){
        this.getProgressTextToSpeak({numberOfStories: numberOfStories + numberOfTests, badgingEnabled:false, badges:[], onEnd: this.goHome.bind(this)});
      }
    });
  }

  goHome(){
    const { fromStudentInbox, returnToInboxURL } = this.props;
    if (fromStudentInbox && returnToInboxURL) {
      window.location.href = returnToInboxURL;
      return;
    }
    this.props.history.push('/student/home');
  }

  goToReadStory(){
    this.props.history.replace('/student/home');
    this.props.history.push('/student/tutor');
  }

  // wrap each piece of text in a div for easier styling
  processDisplayText(textArray){
    return textArray.map((phrase, index) => {
      return (<div className="phrase" key={"phrase" + index}>{phrase}</div>);
    });
  }

  // return different display text for each status case. Although this has some duplication
  // (text which will be the same in all cases which has not been abstracted), this is far more readable than the more abstracted way
  // additionally, this will allow the cases to diverge further in the future without pain
  getDisplayText(){
    const ecouragement = [COPY_STRINGS.keep_up];
    return ecouragement;
  }

  getNumberStoriesFromBadges(numberFromActivities) {
    const { badges } = getUserData();

    let sessions = numberFromActivities;
    if (badges) {
      badges.forEach(badge => {
        if (badge.badgeTypeId === "NUM_SESSIONS" && REWARD_LEVEL_TO_NUM_SESSIONS[badge.rewardLevelId] > sessions) {
          sessions = REWARD_LEVEL_TO_NUM_SESSIONS[badge.rewardLevelId];
        }
      })
    } 

    return sessions;
  }

  // return different display text for each status case. Although this has some duplication
  // (text which will be the same in all cases which has not been abstracted), this is far more readable than the more abstracted way
  // additionally, this will allow the cases to diverge further in the future without pain
  async getProgressTextToSpeak({numberOfStories, badgingEnabled, badges=[], nextBadge, highestBadge={}, onEnd}) {
    if (numberOfStories >= minNumberForStoryCountEstimate) {
      numberOfStories = this.getNumberStoriesFromBadges(numberOfStories);
    }
    const numBadges = badges.filter(x => x.awarded).length;
    const { rewardLevelId } = highestBadge;
    const contentObject = getCopyByGradeband(COPY_STRINGS, 'progress');
    const { welcome, sessions, sessions_estimated, badges_earned, metrics, praise, level, next, stories } = contentObject;
    if (numberOfStories) {
      // speak welcome
      await this.props.speech.speakWithPromise({textToSpeak: getRandomArrayElement(welcome)});

      if (getUserData().bilingualMode) {
        let sessionText;
        if (numberOfStories < minNumberForStoryCountEstimate) {
          sessionText = getRandomArrayElement(sessions({numberOfStories}));
        } else {
          sessionText = getRandomArrayElement(sessions_estimated({numberOfStories}));
        }
        await this.props.speech.speakWithPromise({textToSpeak: sessionText});
      } else {
        const sessionsIndex = getRandomInt(0, sessions.length);
        let sessionText;
        if (numberOfStories < minNumberForStoryCountEstimate) {
          sessionText = sessions({numberOfStories})[sessionsIndex];
        } else {
          sessionText = sessions_estimated({numberOfStories})[sessionsIndex];
        }
        await this.props.speech.speakWithPromise({textToSpeak: sessionText});
      }

      // speak badges earned
      if (badgingEnabled) {
        await this.props.speech.speakWithPromise({textToSpeak: getRandomArrayElement(badges_earned) + numBadges.toString()});
      }

      // speak metrics
      this.setState({badgeSectionHighlighted: true});
      await this.props.speech.speakWithPromise({textToSpeak: getRandomArrayElement(metrics)});
      this.setState({badgeSectionHighlighted: false});

      // speak praise
      let praiseLength = praise.length;
      if (!badgingEnabled || !rewardLevelId) {
         // the last patter in the praise array has a badging dependency
        praiseLength--;
      }
      const praiseIndex = getRandomInt(0, praiseLength);
      let praiseText = praise[praiseIndex];
      if (praiseIndex === praise.length - 1) {
        praiseText += rewardLevelId;
      }
      await this.props.speech.speakWithPromise({textToSpeak: praiseText});

      // speak current badge level
      if (badgingEnabled && rewardLevelId) {
        this.setState({badgeLevelHighlighted: true});
        await this.props.speech.speakWithPromise({textToSpeak: `${level} ${rewardLevelId}`});
        this.setState({badgeLevelHighlighted: false});
      }
    } else {
      await this.props.speech.speakWithPromise({textToSpeak: COPY_STRINGS.no_student_history});
    }
    // speak next badge level
    if (nextBadge && badgingEnabled) {
      this.setState({nextBadgeHighlighted: true});
      await this.props.speech.speakWithPromise({textToSpeak: `${getRandomArrayElement(next)} ${nextBadge}`});
      this.setState({nextBadgeHighlighted: false});
    }
    if (onEnd) onEnd();
  }
  
  componentDidMount(){
    const { fromStudentInbox, session: {badgingEnabled}, closeModal} = this.props;
    document.title = `Student ${badgingEnabled && 'Badges'} Progress - ${GetBrandedAppName()}`;
    if (badgingEnabled) this.props.switchReaderContainerBackground("", "badgingBackground");
    // This is to close the badging ceremony modal when the student navigates from student inbox and badgingEnabled is true
    if (fromStudentInbox && badgingEnabled) {
      closeModal();
    }
  }

  componentWillUnmount(){
    this.props.switchReaderContainerBackground("badgingBackground", "");
  }

  render() {
    let stats = this.state.showStats ? (
      <div className="stats">
        <div className="statContainer left">
          <div className="stat">{this.state.wcpm}</div>
          <div className="statDescription">{COPY_STRINGS.average_speed}</div>
        </div>
        <div className="statContainer right">
          <div className="stat">{this.state.accuracy + "%"}</div>
          <div className="statDescription">{COPY_STRINGS.average_correct}</div>
        </div>
      </div>
    ) : null;

    if (this.props.session.badgingEnabled) {
      return (
        <BadgingProgress
          setLocale={this.props.setLocale}
          speech={this.props.speech}
          fromStudentInbox={this.props.fromStudentInbox}
          getProgressTextToSpeak={this.getProgressTextToSpeak}
          numberOfStories={this.state.numberOfStories}
          wcpm={this.state.wcpm}
          accuracy={this.state.accuracy}
          goHome={this.goHome.bind(this)}
          goToReadStory={this.goToReadStory.bind(this)}
          badgeSectionHighlighted={this.state.badgeSectionHighlighted}
          badgeLevelHighlighted={this.state.badgeLevelHighlighted}
          nextBadgeHighlighted={this.state.nextBadgeHighlighted}
        />
      );
    }

    return (
      <Modal>
        <div className="endOfStoryModal progressModal">
          <div className="alert">{COPY_STRINGS.how_you_are_doing}</div>
          <div className="praise">{this.state.praise}</div>
          <div className="badgeContainer">
            <img className="badge" src={story_badge} alt="" />
          </div>
          {stats}
        </div>
      </Modal>
    );
  }
}
